import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, apiError, fmtDate } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { CheckCircle2 } from "lucide-react";

export default function BillableLogPage() {
    const { user } = useAuth();
    const { siteId } = useParams();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const isHO = user?.role === "HO_ADMIN";

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get(`/sites/${siteId}/billable-log`);
            setRows(data);
        } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
    }
    useEffect(() => { load(); // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteId]);

    async function markBilled(id) {
        const ref = window.prompt("Client invoice reference (optional):");
        try {
            await api.patch(`/billable-log/${id}`, { status: "billed", billed_invoice_ref: ref || null });
            toast.success("Marked billed");
            load();
        } catch (e) { toast.error(apiError(e)); }
    }
    async function markToBill(id) {
        try {
            await api.patch(`/billable-log/${id}`, { status: "to_bill" });
            toast.success("Reverted to to-bill");
            load();
        } catch (e) { toast.error(apiError(e)); }
    }

    const toBillTotal = rows.filter((r) => r.status === "to_bill").reduce((a, r) => a + Number(r.amount || 0), 0);
    const billedTotal = rows.filter((r) => r.status === "billed").reduce((a, r) => a + Number(r.amount || 0), 0);

    return (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-6 sm:py-8">
            <div className="text-xs uppercase tracking-wider text-gray-500">Billable log</div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1">Recoverable expenses</h1>
            <p className="text-sm text-gray-500 mt-1">Every expense flagged recoverable creates a line here. HO Admin marks them billed once the client invoice is raised.</p>

            <div className="mt-6 grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-white border border-gray-100 p-4" data-testid="bl-to-bill">
                    <div className="text-xs uppercase tracking-wider text-gray-500">To bill</div>
                    <div className="mt-1 text-xl font-bold text-[#E02B2B]">₹ {toBillTotal.toLocaleString("en-IN")}</div>
                </div>
                <div className="rounded-xl bg-white border border-gray-100 p-4" data-testid="bl-billed">
                    <div className="text-xs uppercase tracking-wider text-gray-500">Billed</div>
                    <div className="mt-1 text-xl font-bold text-emerald-700">₹ {billedTotal.toLocaleString("en-IN")}</div>
                </div>
            </div>

            <div className="mt-6 rounded-xl bg-white border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead className="bg-[#F4F6F9] text-xs uppercase tracking-wider text-gray-500">
                            <tr><th className="px-3 py-2 text-left">Vendor</th><th className="px-3 py-2 text-left">Invoice #</th><th className="px-3 py-2 text-right">Amount ₹</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Client ref</th><th className="px-3 py-2 text-left">Created</th><th className="px-3 py-2"></th></tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? <tr><td colSpan={7} className="p-6 text-center text-gray-400">Loading…</td></tr> :
                                rows.length === 0 ? <tr><td colSpan={7} className="p-6 text-center text-gray-400" data-testid="no-billable">No recoverable expenses yet.</td></tr> :
                                    rows.map((r) => (
                                        <tr key={r.id} className="hover:bg-gray-50" data-testid={`billable-row-${r.id}`}>
                                            <td className="px-3 py-2 font-medium text-[#1E3355]">{r.vendor_name}</td>
                                            <td className="px-3 py-2">{r.invoice_number || "—"}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">{Number(r.amount).toLocaleString("en-IN")}</td>
                                            <td className="px-3 py-2">
                                                {r.status === "billed" ? (
                                                    <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-medium"><CheckCircle2 className="w-3.5 h-3.5" /> Billed</span>
                                                ) : (
                                                    <span className="text-xs text-red-700 font-medium">To bill</span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-xs">{r.billed_invoice_ref || "—"}</td>
                                            <td className="px-3 py-2 text-xs">{fmtDate(r.created_at)}</td>
                                            <td className="px-3 py-2 text-right">
                                                {isHO && (r.status === "to_bill"
                                                    ? <button onClick={() => markBilled(r.id)} className="rounded-md bg-emerald-600 text-white px-2 py-1 text-xs font-medium" data-testid={`bill-mark-${r.id}`}>Mark billed</button>
                                                    : <button onClick={() => markToBill(r.id)} className="rounded-md border border-gray-200 px-2 py-1 text-xs" data-testid={`bill-revert-${r.id}`}>Revert</button>)}
                                            </td>
                                        </tr>
                                    ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
